import React, { useContext, useEffect, useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import MyContext from "../ContextApi/MyContext";
import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet-async";
import { FaUserCircle, FaArrowRight, FaCommentAlt } from "react-icons/fa";

export default function Blog() {
  const { filterPosts, posts, uniqueCategory, setCategory, category } = useContext(MyContext);
  const location = useLocation();
  const { slug } = useParams();

  useEffect(() => {
    AOS.init({ once: true });
  }, []);

  const primaryColor = "#684df4";
  const isDetailPage = !!slug;

  // Find current post when on detail page
  const currentPost = useMemo(() => {
    if (!isDetailPage) return null;
    return posts?.find((p) => p.slug === slug);
  }, [posts, slug, isDetailPage]);

  // Auto-detect the active category based on displayed posts
  const activeCategory = useMemo(() => {
    if (isDetailPage && currentPost) {
      // On detail page, highlight the post's category
      return currentPost.category;
    } else if (filterPosts && filterPosts.length > 0) {
      // On list page, highlight the category of displayed posts
      // If user has selected a category, use that
      if (category) {
        return category;
      }
      // Otherwise, find the most common category in filtered posts
      const categoryCount = {};
      filterPosts.forEach(post => {
        categoryCount[post.category] = (categoryCount[post.category] || 0) + 1;
      });
      return Object.keys(categoryCount).sort((a, b) => categoryCount[b] - categoryCount[a])[0];
    }
    return null;
  }, [isDetailPage, currentPost, filterPosts, category]);

  // Curated tech-related images from Unsplash
  const techImages = [
    "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1487058792275-0ad4aaf24ca7?w=800&h=400&fit=crop",
  ];

  const getPostImage = (post, index) => {
    if (post.image) return post.image;

    let imageIndex = index;
    if (post._id) {
      const idSum = post._id.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
      imageIndex = idSum % techImages.length;
    } else {
      imageIndex = index % techImages.length;
    }
    return techImages[imageIndex];
  };

  return (
    <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh", overflowX: "hidden" }}>
      <Helmet>
        <title>Tech Insights & Blog - Technic Mentors</title>
        <link rel="canonical" href="https://technicmentors.com/blog/" />
      </Helmet>

      {/* Hero Header */}
      <div className="allHeaderBg" style={{ background: primaryColor, padding: "50px 0" }}>
        <div className="container text-center text-white">
          <h1 className="fw-bold animate__animated animate__zoomIn" style={{ fontSize: "2.5rem" }}>
            Tech Blog & Insights
          </h1>
        </div>
      </div>

      <div className="container my-5">
        <div className="row g-4">
          {/* Category Sidebar */}
          <div className="col-lg-3">
            <div
              className="p-4 rounded-3 shadow-sm bg-white border"
              style={{ position: "sticky", top: "100px" }}
              data-aos="fade-right"
            >
              <h5 className="mb-4 fw-bold" style={{ color: "#333" }}>
                Categories
              </h5>
              <div className="d-grid gap-2">
                {posts &&
                  [...uniqueCategory].map((cat) => {
                    const isActive = activeCategory === cat;

                    return (
                      <button
                        key={cat}
                        className="btn text-start fw-medium border-0 ps-3 py-2 rounded"
                        onClick={() => setCategory(cat)}
                        style={{
                          color: isActive ? "#ffffff" : "#6c757d",
                          backgroundColor: isActive ? primaryColor : "transparent",
                          transition: "all 0.3s ease",
                          fontWeight: isActive ? "600" : "500",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.target.style.backgroundColor = "#f0f0f0";
                            e.target.style.color = primaryColor;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.target.style.backgroundColor = "transparent";
                            e.target.style.color = "#6c757d";
                          }
                        }}
                      >
                        {cat}
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>

          {/* Blog Posts Grid */}
          <div className="col-lg-9">
            <div className="row g-4">
              {filterPosts.map((post, index) => (
                <div className="col-md-6 col-lg-4" key={post._id} data-aos="fade-up">
                  <div
                    className="card h-100 border-0 shadow-sm"
                    style={{ borderRadius: "12px", background: "#fff", overflow: "hidden" }}
                  >
                    <div style={{ height: "180px", overflow: "hidden", background: "#eee" }}>
                      <img
                        src={getPostImage(post, index)}
                        alt={post.title}
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                        onError={(e) => {
                          e.target.src = techImages[0];
                        }}
                      />
                    </div>

                    <div className="card-body p-4 d-flex flex-column">
                      <div className="d-flex align-items-center mb-2 text-muted" style={{ fontSize: "0.8rem" }}>
                        <FaUserCircle className="me-2" />
                        <span>admin</span>
                      </div>

                      <Link to={`/blog/${post.slug}`} className="text-decoration-none">
                        <h5
                          className="card-title fw-bold mb-3 text-dark"
                          style={{
                            fontSize: "1.1rem",
                            lineHeight: "1.4",
                            height: "3.2rem",
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: "2",
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {post.title}
                        </h5>
                      </Link>

                      <p
                        className="card-text text-muted mb-4"
                        style={{
                          fontSize: "0.85rem",
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: "3",
                          WebkitBoxOrient: "vertical",
                          lineHeight: "1.6",
                        }}
                      >
                        {post.excerpt || `Introduction: ${post.title} is at the core of many challenges within an organization today.`}
                      </p>

                      <div
                        className="d-flex justify-content-between align-items-center pt-3 mt-auto"
                        style={{ borderTop: "1px solid #f0f0f0", fontSize: "0.75rem", fontWeight: "700" }}
                      >
                        <Link
                          to={`/blog/${post.slug}`}
                          className="text-dark text-decoration-none d-flex align-items-center"
                        >
                          Read More <FaArrowRight className="ms-1" size={10} />
                        </Link>

                        <span className="text-muted d-flex align-items-center" style={{ cursor: "pointer" }}>
                          <FaCommentAlt className="me-1" size={10} /> Post a Comment
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}